<template>
  <!--
    Nuxt 4 renders this file INSTEAD of app.vue/NuxtLayout, so nothing above it
    supplies the Vuetify root — hence the <v-app> here. Without it the page
    renders unstyled and `.v-application` never exists (which is also why a probe
    that keys "did the app render?" off `.v-application` reports false on an
    error page).
  -->
  <v-app>
    <v-main>
      <div :style="hideWhenLoading">
        <v-row justify="center">
          <v-col cols="12" sm="8" md="6">
            <v-card class="py-4 d-flex justify-center">
              <p style="font-family: Orbitron; font-size: 30pt">cynque</p>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-center">
          <h1>{{ errorDisplay.error }}</h1>
          <h2>{{ errorDisplay.message }}</h2>
          <a href="#" @click.prevent="goToDashboard"> Dashboard </a>
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import { usePageParamsStore } from '~/stores/pageParams'
import { useUserStore } from '~/stores/user'
/* eslint-disable no-console */

/**
 * The application error page.
 *
 * ⚠ This lives at the PROJECT ROOT, not in layouts/. Nuxt 2 took its error page
 * from `layouts/error.vue`; Nuxt 4 takes it from `~/error.vue` and treats
 * anything under layouts/ as an ordinary NAMED layout. This file was ported to
 * Pinia during the migration but left in layouts/, so nothing could reach it —
 * every showError() rendered Nuxt's generic built-in page instead.
 *
 * ⚠ Leaving this page requires clearError(), not a bare router.push: the error
 * state is what keeps this component mounted, so navigating without clearing it
 * leaves the error page on screen over the route you went to.
 */
function describeError(error) {
  const statusCode = error?.statusCode
  switch (statusCode) {
    case 401:
      return {
        error: '401',
        message: 'Not Authorized - ' + (error?.message ?? ''),
      }
    case 403:
      return { error: '403', message: 'Forbidden' }
    case 404:
      return { error: '404', message: 'Not Found' }
    case 405:
      return { error: '405', message: 'Method Not Allowed' }
    default:
      return {
        error: statusCode,
        message: error?.message ? error.message : 'An error occurred',
      }
  }
}

export default {
  props: {
    error: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    // Nuxt 2's `head()` option is gone. It also used to hand `title` an OBJECT,
    // so the tab read "[object Object]" — give it the string it always wanted.
    useHead({
      title: computed(() => {
        const { error, message } = describeError(props.error)
        return `${error ?? 'Error'} ${message}`.trim()
      }),
    })
  },
  data() {
    return {
      hideWhenLoading: 'display:none',
    }
  },
  computed: {
    errorDisplay() {
      return describeError(this.error)
    },
  },
  mounted() {
    const fn = '[error.vue] mounted() |'

    setTimeout(() => {
      // I don't really understand why this is necessary in this one case. Flashes without. JR
      this.hideWhenLoading = ''
    }, 200)
    console.debug(`${fn} Fired`)
    if (this.error?.statusCode === 403) {
      clearError({ redirect: '/Login' })
      return
    }
    if (this.error?.statusCode === 401) {
      // Might want to do this only for jwt-expired.
      console.debug(`${fn} ${this.error.statusCode} error. Logging out user`)
      useUserStore().logout()
      console.debug(`${fn} Calling setIsExpiredSession`)
      usePageParamsStore().setIsExpiredSession()
      console.debug(`${fn} Clearing error and going to Login`)
      clearError({ redirect: '/Login' })
    }
  },
  methods: {
    goToDashboard() {
      clearError({ redirect: '/Dashboard' })
    },
  },
}
</script>

<style scoped>
/*
 * Pin these to Roboto (bundled with a real Thin/100 face via @fontsource/roboto)
 * rather than inheriting html's Orbitron. Orbitron ships no weight below 400, so
 * font-weight: 100 only renders light on a font that actually has a Thin face.
 * See documentation and the light-error-code investigation for the full chain.
 */
h1,
h2 {
  font-family: 'Roboto', sans-serif;
}
h1 {
  font-size: 9.2rem;
  font-weight: 100;
  height: 11rem;
}
h2 {
  font-size: 2rem;
  font-weight: 300;
  height: 10rem;
}
</style>
